import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "../mock";

const Gallery = () => {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback(
    (e) => {
      e && e.stopPropagation();
      setIndex((i) => (i === 0 ? GALLERY.length - 1 : i - 1));
    },
    []
  );
  const next = useCallback(
    (e) => {
      e && e.stopPropagation();
      setIndex((i) => (i === GALLERY.length - 1 ? 0 : i + 1));
    },
    []
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, prev, next]);

  return (
    <section id="gallery" className="relative bg-[#0a0410] py-24 md:py-32 border-t border-white/5">
      <div className="max-w-[1300px] mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-[#c9a24a] tracking-[0.3em] uppercase text-xs mb-3">Press Photos</p>
          <h2 className="heading-display gold-text text-4xl md:text-6xl">THE GALLERY</h2>
        </div>

        {/* Masonry-style columns */}
        <div className="columns-2 md:columns-3 gap-4 md:gap-6 [column-fill:_balance]">
          {GALLERY.map((img, i) => (
            <button
              key={img.id}
              onClick={() => setIndex(i)}
              className="group relative mb-4 md:mb-6 block w-full overflow-hidden rounded-lg ring-1 ring-[#d4af37]/15 break-inside-avoid"
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0410]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="absolute inset-0 ring-1 ring-inset ring-[#d4af37]/0 group-hover:ring-[#d4af37]/50 rounded-lg transition-all duration-500" />
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/95 backdrop-blur-sm p-4"
          onClick={close}
        >
          <button
            onClick={close}
            className="absolute top-5 right-5 text-white/80 hover:text-[#e8c65a] transition-colors z-10"
            aria-label="Close"
          >
            <X size={32} />
          </button>

          <button
            onClick={prev}
            className="absolute left-3 md:left-8 flex items-center justify-center w-12 h-12 rounded-full border border-[#d4af37]/40 text-[#e8c65a] hover:bg-[#d4af37] hover:text-[#2a0e47] transition-all duration-300"
            aria-label="Previous"
          >
            <ChevronLeft size={26} />
          </button>

          <img
            src={GALLERY[index].src}
            alt={GALLERY[index].alt}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg ring-1 ring-[#d4af37]/30 shadow-2xl"
          />

          <button
            onClick={next}
            className="absolute right-3 md:right-8 flex items-center justify-center w-12 h-12 rounded-full border border-[#d4af37]/40 text-[#e8c65a] hover:bg-[#d4af37] hover:text-[#2a0e47] transition-all duration-300"
            aria-label="Next"
          >
            <ChevronRight size={26} />
          </button>

          <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#c9a24a] text-xs tracking-[0.2em]">
            {index + 1} / {GALLERY.length}
          </span>
        </div>
      )}
    </section>
  );
};

export default Gallery;
